import type { ReactElement } from 'react';
import { Link } from 'react-router';
import {
  datoIOslo,
  erForsinket,
  leveringsfrist,
  periodenavn,
  type Maned,
  type OppgaveDto,
  type BudsjettoppgaveDto,
  type Prognoserytme,
  type RapportoppgaverDto,
  type VarseloppgaverDto,
  type AksjonsoppgaverDto,
  type InvesteringsoppgaverDto,
  type KontrolloppgaverDto,
  SAKSTYPENAVN,
  type SaksoppgaverDto,
} from '@es/shared';
import {
  useBudsjettoppgaver,
  useOppgaver,
  useRapportoppgaver,
  useVarseloppgaver,
  useAksjonsoppgaver,
  useInvesteringsoppgaver,
  useKontrolloppgaver,
} from '../api/sporringer.js';
import { useSaksoppgaver } from '../sak/sporringer.js';
import { useValgtVirksomhet, type Omfang } from '../felles/virksomhet.js';
import { Kort } from '../komponenter/Kort.js';
import { Feilmelding, Laster } from '../komponenter/Tilstand.js';
import { formaterTidspunkt } from './prognose/endringslogg.js';
import { formaterFrist } from './prognose/leveringer.js';
import { varseloppgavetekst } from './prognose/varsler.js';
import { budsjettoppgavetekst } from './budsjett/budsjett.js';
import { fristtekst, oppgavelenke, rapportoppgavetekst } from './rapportering/rapportflyt.js';

const LENKE =
  'inline-flex min-h-11 shrink-0 items-center rounded-md border border-kant px-4 text-sm font-medium text-merke hover:bg-merke-svak focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-merke';

/** Til prognosen for et gitt år — oppgaven kan gjelde desember i fjor. */
const tilPrognosen = (ar: number, levering = false): string =>
  `/prognose?ar=${String(ar)}${levering ? '#levering' : ''}`;

/** «Det som venter på deg i prognosen og i rapportene.» — for modulene som er på. */
function beskrivelse(moduler: readonly (string | false)[]): string {
  const deler = moduler.filter((m): m is string => m !== false);
  const siste = deler.pop() ?? '';
  return `Det som venter på deg i ${deler.length === 0 ? '' : `${deler.join(', i ')} og i `}${siste}.`;
}

/**
 * Hva som venter på den som er logget inn.
 *
 * Utledet av leveringene, rapportene og budsjettet, ikke lagret: er leveringen
 * godkjent, kapitlet markert som ferdig eller detaljbudsjettet i balanse, er
 * oppgaven borte. Hver oppgave er en lenke rett dit — en oppgave man må lete
 * fram selv, er bare en påminnelse.
 *
 * `prognose`, `rapportering` og `budsjett` er om modulene er på. Oppgavene fra
 * en modul som er av, hentes ikke.
 */
export function Oppgaver({
  prognose,
  rapportering,
  budsjett,
  moter = false,
  investeringer = false,
  vedlikehold = false,
  saker = false,
}: {
  prognose: boolean;
  rapportering: boolean;
  budsjett: boolean;
  /** Om møter og grupper er på: da er de åpne aksjonspunktene oppgaver. */
  moter?: boolean;
  /** Om investeringene er på, og kalleren følger dem opp. */
  investeringer?: boolean;
  /** Om vedlikeholdet er på: da er kontrollene som forfaller, oppgaver. */
  vedlikehold?: boolean;
  /** Om noen modul med saker er på: sakene du er ansvarlig for eller er nevnt i. */
  saker?: boolean;
}): ReactElement {
  const oppgaver = useOppgaver(prognose);
  const varseloppgaver = useVarseloppgaver(prognose);
  const aksjonsoppgaver = useAksjonsoppgaver(moter);
  const aksjoner = aksjonsoppgaver.data?.oppgaver ?? [];
  const rapportoppgaver = useRapportoppgaver(rapportering);
  const investeringsoppgaver = useInvesteringsoppgaver(investeringer);
  const investeringsprosjekter = investeringsoppgaver.data?.prosjekter ?? [];
  const kontrolloppgaver = useKontrolloppgaver(vedlikehold);
  const kontroller = kontrolloppgaver.data?.kontroller ?? [];
  const saksoppgaver = useSaksoppgaver(saker);
  const sakene = saksoppgaver.data?.saker ?? [];
  const budsjettoppgaver = useBudsjettoppgaver(budsjett);
  const data = oppgaver.data;
  const rapport = rapportoppgaver.data?.oppgaver ?? [];
  const budsjettet = budsjettoppgaver.data?.oppgaver ?? [];
  const varsler = varseloppgaver.data?.oppgaver ?? [];
  const laster =
    (prognose && oppgaver.isPending) ||
    (rapportering && rapportoppgaver.isPending) ||
    (budsjett && budsjettoppgaver.isPending) ||
    (moter && aksjonsoppgaver.isPending) ||
    (investeringer && investeringsoppgaver.isPending) ||
    (vedlikehold && kontrolloppgaver.isPending) ||
    (saker && saksoppgaver.isPending);
  const antall =
    (data?.oppgaver.length ?? 0) +
    varsler.length +
    rapport.length +
    budsjettet.length +
    aksjoner.length +
    (investeringsprosjekter.length > 0 ? 1 : 0) +
    (kontroller.length > 0 ? 1 : 0) +
    sakene.length;
  const feil =
    oppgaver.isError ||
    varseloppgaver.isError ||
    rapportoppgaver.isError ||
    budsjettoppgaver.isError ||
    aksjonsoppgaver.isError ||
    investeringsoppgaver.isError ||
    kontrolloppgaver.isError ||
    saksoppgaver.isError;

  return (
    <Kort
      tittel="Oppgaver"
      beskrivelse={beskrivelse([
        prognose && 'prognosen',
        rapportering && 'rapportene',
        budsjett && 'budsjettet',
        moter && 'møtene',
        investeringer && 'investeringene',
        vedlikehold && 'vedlikeholdet',
      ])}
      className="mt-8"
    >
      {laster ? <Laster hva="oppgavene" /> : null}
      {oppgaver.isError ? <Feilmelding feil={oppgaver.error} /> : null}
      {varseloppgaver.isError ? <Feilmelding feil={varseloppgaver.error} /> : null}
      {rapportoppgaver.isError ? <Feilmelding feil={rapportoppgaver.error} /> : null}
      {budsjettoppgaver.isError ? <Feilmelding feil={budsjettoppgaver.error} /> : null}
      {aksjonsoppgaver.isError ? <Feilmelding feil={aksjonsoppgaver.error} /> : null}
      {investeringsoppgaver.isError ? <Feilmelding feil={investeringsoppgaver.error} /> : null}
      {kontrolloppgaver.isError ? <Feilmelding feil={kontrolloppgaver.error} /> : null}
      {saksoppgaver.isError ? <Feilmelding feil={saksoppgaver.error} /> : null}
      {!laster && antall === 0 && !feil ? (
        <p className="text-sm text-tekst-dempet">Ingenting venter på deg nå.</p>
      ) : null}
      {antall > 0 ? (
        <ul className="divide-y divide-kant">
          {data?.oppgaver.map((o) => (
            <Oppgave key={nokkel(o)} oppgave={o} rytme={data.rytme} fristdag={data.fristdag} />
          ))}
          {varsler.map((o) => (
            <Varseloppgave key={`varsel:${o.virksomhetId}`} oppgave={o} />
          ))}
          {rapport.map((o) => (
            <Rapportoppgave
              key={`${o.oppgave}:${o.rapportId}:${'omradeId' in o ? o.omradeId : ''}:${'utsendelseId' in o ? o.utsendelseId : ''}`}
              oppgave={o}
            />
          ))}
          {aksjoner.map((o) => (
            <Aksjonsoppgave key={`aksjon:${o.id}`} oppgave={o} />
          ))}
          {investeringsoppgaver.data && investeringsprosjekter.length > 0 ? (
            <Investeringsoppgave oppgaver={investeringsoppgaver.data} />
          ) : null}
          {sakene.map((s) => (
            <Saksoppgave key={`sak:${s.sakstype}:${s.sakId}`} sak={s} />
          ))}
          {kontrolloppgaver.data && kontroller.length > 0 ? (
            <Kontrolloppgave oppgaver={kontrolloppgaver.data} />
          ) : null}
          {budsjettet.map((o) => (
            <Budsjettoppgave
              key={`${o.oppgave}:${String(o.ar)}:${o.oppgave === 'fordel' ? o.omradeId : o.virksomhetId}`}
              oppgave={o}
            />
          ))}
        </ul>
      ) : null}
    </Kort>
  );
}

const LENKETEKST: Record<RapportoppgaverDto['oppgaver'][number]['oppgave'], string> = {
  skriv: 'Til rapporten',
  folg_opp: 'Til rapporten',
  foresla: 'Foreslå tekst',
  les: 'Les og kommenter',
  svar: 'Til kommentarene',
};

/** En oppgave i en rapport: skrive, følge opp, foreslå — eller i den politiske behandlingen. */
function Rapportoppgave({
  oppgave: o,
}: {
  oppgave: RapportoppgaverDto['oppgaver'][number];
}): ReactElement {
  const idag = datoIOslo(new Date());
  const { tittel, detalj } = rapportoppgavetekst(o);
  // Den som følger opp, har ingen frist selv — fristen er for dem som skriver.
  const frist = o.frist === null || o.oppgave === 'folg_opp' ? null : o.frist;
  const forsinket = frist !== null && idag > frist;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-tekst">
          {tittel}
          {forsinket && o.oppgave === 'skriv' ? (
            <span className="rounded-full bg-negativ-svak px-2.5 py-0.5 text-xs font-semibold">
              Forsinket
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">{detalj}</p>
        {frist === null ? null : (
          <p
            className={
              forsinket && o.oppgave === 'skriv'
                ? 'mt-0.5 text-sm font-medium text-negativ'
                : 'mt-0.5 text-sm text-tekst-dempet'
            }
          >
            {fristtekst(frist, idag)}
          </p>
        )}
      </div>
      <Link to={oppgavelenke(o)} className={LENKE}>
        {LENKETEKST[o.oppgave]}
      </Link>
    </li>
  );
}

/** Uvanlige posteringer lederen bør se på før prognosen leveres. */
function Varseloppgave({
  oppgave: o,
}: {
  oppgave: VarseloppgaverDto['oppgaver'][number];
}): ReactElement {
  const { settOmfang } = useValgtVirksomhet();
  const { tittel, detalj } = varseloppgavetekst(o);
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-tekst">{tittel}</p>
        <p className="mt-0.5 text-sm text-tekst-dempet">{detalj}</p>
      </div>
      <Link
        to={`/prognose?ar=${String(o.ar)}#varsler`}
        className={LENKE}
        onClick={() => {
          settOmfang({ type: 'virksomhet', id: o.virksomhetId });
        }}
      >
        Til posteringene
      </Link>
    </li>
  );
}

/** Et åpent aksjonspunkt fra et møte, med frist. */
function Aksjonsoppgave({
  oppgave: o,
}: {
  oppgave: AksjonsoppgaverDto['oppgaver'][number];
}): ReactElement {
  const idag = datoIOslo(new Date());
  const forfalt = o.frist !== null && idag > o.frist;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-tekst">
          <Link
            to={`/sak/aksjonspunkt/${o.id}`}
            className="underline decoration-kant-sterk hover:text-merke"
          >
            {o.tekst}
          </Link>
          {forfalt ? (
            <span className="rounded-full bg-negativ-svak px-2.5 py-0.5 text-xs font-semibold">
              Forsinket
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">
          Aksjonspunkt fra {o.mote.tittel} — {o.gruppe.navn}
        </p>
        {o.frist === null ? null : (
          <p
            className={
              forfalt
                ? 'mt-0.5 text-sm font-medium text-negativ'
                : 'mt-0.5 text-sm text-tekst-dempet'
            }
          >
            {fristtekst(o.frist, idag)}
          </p>
        )}
      </div>
      <Link to={`/moter?gruppe=${o.gruppe.id}&mote=${o.mote.id}`} className={LENKE}>
        Til møtet
      </Link>
    </li>
  );
}

/** Investeringsprosjektene som venter på kommunalsjefens prognose. */
function Investeringsoppgave({ oppgaver: o }: { oppgaver: InvesteringsoppgaverDto }): ReactElement {
  const antall = o.prosjekter.length;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-tekst">
          Meld prognosen for{' '}
          {antall === 1 ? 'ett investeringsprosjekt' : `${String(antall)} investeringsprosjekter`}
          {o.periode === null ? '' : ` for ${periodenavn(o.rytme, o.periode)}`}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">
          {o.prosjekter.map((p) => `${p.kode} ${p.navn}`).join(', ')}
        </p>
      </div>
      <Link to="/investeringer" className={LENKE}>
        Til investeringene
      </Link>
    </li>
  );
}

/**
 * En sak du er ansvarlig for, eller er nevnt i. Avvik, kontroller og
 * arbeidsordrer hører til vedlikeholdsappen, som er en egen side; resten åpnes her.
 */
function Saksoppgave({ sak: s }: { sak: SaksoppgaverDto['saker'][number] }): ReactElement {
  const vedlikehold =
    s.sakstype === 'avvik' || s.sakstype === 'kontroll' || s.sakstype === 'arbeidsordre';
  const til = `${vedlikehold ? '/vedlikehold' : ''}/sak/${s.sakstype}/${s.sakId}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-tekst">{s.tittel}</p>
        <p className="mt-0.5 text-sm text-tekst-dempet">
          {SAKSTYPENAVN[s.sakstype]} {s.nummer} ·{' '}
          {s.grunn === 'omtalt' ? 'Du er nevnt i diskusjonen' : 'Du er ansvarlig'}
        </p>
      </div>
      {vedlikehold ? (
        <a href={til} className={LENKE}>
          Til saken<span className="sr-only"> {s.nummer}</span>
        </a>
      ) : (
        <Link to={til} className={LENKE}>
          Til saken<span className="sr-only"> {s.nummer}</span>
        </Link>
      )}
    </li>
  );
}

/**
 * Kontrollene i vedlikeholdsappen som er over fristen eller forfaller innen 30
 * dager. Én linje, med lenke til appen: den er en egen side, ikke en rute her.
 */
function Kontrolloppgave({ oppgaver: o }: { oppgaver: KontrolloppgaverDto }): ReactElement {
  const over = o.kontroller.filter((k) => k.fristtilstand === 'over_fristen').length;
  const snart = o.kontroller.length - over;
  const deler = [
    over === 0 ? null : `${String(over)} over fristen`,
    snart === 0 ? null : `${String(snart)} innen 30 dager`,
  ].filter((x) => x !== null);
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-sm font-medium text-tekst">
          {o.kontroller.length === 1 ? 'En kontroll forfaller' : 'Kontroller forfaller'}:{' '}
          {deler.join(', ')}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">
          {o.kontroller.map((k) => `${k.type}, ${k.objekt.navn}`).join(' · ')}
        </p>
      </div>
      <a href="/vedlikehold/kontroller" className={LENKE}>
        Til kontrollene
      </a>
    </li>
  );
}

/** En oppgave i budsjettet: fordele områdets ramme, eller budsjettere virksomheten. */
function Budsjettoppgave({ oppgave: o }: { oppgave: BudsjettoppgaveDto }): ReactElement {
  const idag = datoIOslo(new Date());
  const { tittel, detalj, lenke, lenketekst } = budsjettoppgavetekst(o);
  const forsinket = o.frist !== null && idag > o.frist;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-tekst">
          {tittel}
          {forsinket ? (
            <span className="rounded-full bg-negativ-svak px-2.5 py-0.5 text-xs font-semibold">
              Forsinket
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">{detalj}</p>
        {o.frist === null ? null : (
          <p
            className={
              forsinket
                ? 'mt-0.5 text-sm font-medium text-negativ'
                : 'mt-0.5 text-sm text-tekst-dempet'
            }
          >
            {fristtekst(o.frist, idag, ' for innspill')}
          </p>
        )}
      </div>
      <Link to={lenke} className={LENKE}>
        {lenketekst}
      </Link>
    </li>
  );
}

const nokkel = (o: OppgaveDto): string =>
  o.type === 'mangler' ? `mangler:${o.kommunalomradeId}` : `${o.type}:${o.virksomhetId}`;

function Oppgave({
  oppgave: o,
  rytme,
  fristdag,
}: {
  oppgave: OppgaveDto;
  rytme: Prognoserytme;
  fristdag: number | null;
}): ReactElement {
  const { settOmfang } = useValgtVirksomhet();
  const idag = datoIOslo(new Date());

  // Alle oppgavene gjelder en periode som ikke er levert — eller er sendt
  // tilbake, som teller likt. Fristen gjelder dem alle.
  const frist = (ar: number, periode: Maned): { tekst: string; forsinket: boolean } | null => {
    const dato = leveringsfrist(ar, periode, fristdag);
    if (dato === null) return null;
    const forsinket = erForsinket('ikke_levert', dato, idag);
    return {
      forsinket,
      tekst: `${forsinket ? 'Fristen var' : 'Frist'} ${formaterFrist(dato, ar)}.`,
    };
  };

  const rad = (
    tittel: string,
    detalj: ReactElement | string,
    lenke: { tekst: string; til: string; omfang: Omfang },
    fristen: { tekst: string; forsinket: boolean } | null = null,
  ): ReactElement => (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-tekst">
          {tittel}
          {fristen?.forsinket ? (
            <span className="rounded-full bg-negativ-svak px-2.5 py-0.5 text-xs font-semibold">
              Forsinket
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-sm text-tekst-dempet">{detalj}</p>
        {fristen ? (
          <p
            className={
              fristen.forsinket
                ? 'mt-0.5 text-sm font-medium text-negativ'
                : 'mt-0.5 text-sm text-tekst-dempet'
            }
          >
            {fristen.tekst}
          </p>
        ) : null}
      </div>
      <Link
        to={lenke.til}
        className={LENKE}
        onClick={() => {
          settOmfang(lenke.omfang);
        }}
      >
        {lenke.tekst}
      </Link>
    </li>
  );

  switch (o.type) {
    case 'godkjenn':
      return rad(
        `Godkjenn ${periodenavn(rytme, o.periode)} for ${o.virksomhetNavn}`,
        <>
          Levert{o.levert.av === null ? '' : ` av ${o.levert.av}`}{' '}
          <time dateTime={o.levert.tidspunkt}>{formaterTidspunkt(o.levert.tidspunkt)}</time>
        </>,
        {
          tekst: 'Til leveringene',
          til: tilPrognosen(o.ar),
          omfang: { type: 'omrade', id: o.kommunalomradeId },
        },
      );
    case 'rett':
      return rad(
        `Prognosen for ${periodenavn(rytme, o.periode)} ble sendt tilbake — ${o.virksomhetNavn}`,
        `${o.behandlet.av ?? 'Kommunalsjefen'}: ${o.behandlet.kommentar ?? 'ingen begrunnelse'}`,
        {
          tekst: 'Rett og lever på nytt',
          til: tilPrognosen(o.ar, true),
          omfang: { type: 'virksomhet', id: o.virksomhetId },
        },
        frist(o.ar, o.periode),
      );
    case 'lever':
      return rad(
        `Lever prognosen for ${periodenavn(rytme, o.periode)} — ${o.virksomhetNavn}`,
        o.ar === new Date().getFullYear()
          ? 'Regnskapet for perioden er ført.'
          : `Gjelder ${String(o.ar)}.`,
        {
          tekst: 'Til prognosen',
          til: tilPrognosen(o.ar, true),
          omfang: { type: 'virksomhet', id: o.virksomhetId },
        },
        frist(o.ar, o.periode),
      );
    case 'mangler': {
      const antall = o.virksomheter.length;
      const ar = o.virksomheter[0]?.ar ?? new Date().getFullYear();
      const forsinkede = o.virksomheter.filter((v) => frist(v.ar, v.periode)?.forsinket).length;
      return rad(
        `${String(antall)} ${antall === 1 ? 'virksomhet' : 'virksomheter'} i ${o.kommunalomradeNavn} har ikke levert`,
        o.virksomheter
          .map(
            (v) =>
              `${v.navn} (${periodenavn(rytme, v.periode)}${
                frist(v.ar, v.periode)?.forsinket ? ', forsinket' : ''
              })`,
          )
          .join(', '),
        {
          tekst: 'Til leveringene',
          til: tilPrognosen(ar),
          omfang: { type: 'omrade', id: o.kommunalomradeId },
        },
        forsinkede === 0
          ? null
          : {
              forsinket: true,
              tekst: `${String(forsinkede)} av dem er forsinket.`,
            },
      );
    }
  }
}
